import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { getParamsFromSearch } from 'core/utils/helpers/getParamsFromSearch';
import { fetchSearchProducts } from 'core/actions/ocapi';
import { EXTRA_REWARDS_FILTER, EXTRA_REWARDS_FILTER_ID } from 'core/modules/ProductSearchResults/utils/keys';
import { triggerPsrClearResult } from '../actions';
import { PRODUCT_SEARCH_RESULTS_GET_SUCCESS } from '../actions/types';

import {
  selectProductSearchResultInitialFacets, selectProductSearchResultsConfig,
  selectProductSearchResultsIsLoading, selectProductSearchResultsIsLoaded,
} from '../selectors';

const NO_EXTRA_REWARDS_MERCHANTS_MATCHES = 'noExtraRewardsMerchantsMatches';

export const useGetProductSearchResults = (filters) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const config = useSelector(selectProductSearchResultsConfig);
  const isLoading = useSelector(selectProductSearchResultsIsLoading);
  const isLoaded = useSelector(selectProductSearchResultsIsLoaded);
  const initialFacets = useSelector(selectProductSearchResultInitialFacets);

  const {
    params, loadOptions, updateOptions, filterOptions,
  } = config.api;
  const { limit: defaultLimit, sort_by, offset: defaultOffset } = params;

  const { keywords: q, category_id = '' } = getParamsFromSearch(location.search);
  const getInitialParams = () => {
    const params = {
      limit: defaultLimit, q, sort_by, offset: defaultOffset, f: {},
    };

    if (category_id) {
      params.f.categories = [category_id];
    }

    return params;
  };

  const searchParamsRef = useRef(getInitialParams());

  const handleFetchingData = (params, options) => {
    const currentParams = cloneDeep(params);
    currentParams.f = updateFilterParam(currentParams.f);

    // Fetch data only on page load or if search params are changed
    const isOnPageLoad = options.success === PRODUCT_SEARCH_RESULTS_GET_SUCCESS;
    const isSameParams = isEqual(searchParamsRef.current, currentParams);
    if (!isOnPageLoad && isSameParams) {
      return;
    }

    searchParamsRef.current = cloneDeep(currentParams);
    currentParams.f = handleExtraRewardsFiltering(currentParams.f);

    // Clear search result if there is no extra rewards merchants matches in current filter combination
    if (currentParams.f === NO_EXTRA_REWARDS_MERCHANTS_MATCHES) {
      dispatch(triggerPsrClearResult());
      return;
    }

    currentParams.f = stringifyFilterParamForApiCall(currentParams.f);
    dispatch(fetchSearchProducts(currentParams, options));

    function handleExtraRewardsFiltering(filterParam = {}) {
      /*
      API doesn't know how to do extra rewards filtering and can only return extra rewards merchant ids
      so we should do extra rewards filtering on FE.
      Filtering between different facets uses 'AND' logic when product should be in filter A AND filter B.
      So the filter logic here is (CLPL-17325):
      1. If user doesn't check extra rewards filter we continue showing current results
         without additional FE extra-rewards filtering.
      2. If user checked extra rewards filter:
         a. if there are no extra rewards merchants ids we don't show any results
         b. if user didn't check any merchants in 'Stores' filter we get products for every extra rewards merchant id.
         c. if user checked some merchants in 'Stores' filter we filter out from it any ids that are not extra rewards.
         d. if user checked some merchants in 'Stores' filter but none of them is extra rewards,
            we don't show any results because filters are mutually exclusive in this case.
      */
      const isExtraRewardsFilterChecked = filterParam?.[EXTRA_REWARDS_FILTER]?.includes(EXTRA_REWARDS_FILTER_ID);
      const extraRewardsIds = initialFacets?.[EXTRA_REWARDS_FILTER]?.values.map(({ id }) => id);

      // 1
      if (!isExtraRewardsFilterChecked) {
        return filterParam;
      }

      // 2a
      if (isEmpty(extraRewardsIds)) {
        return NO_EXTRA_REWARDS_MERCHANTS_MATCHES;
      }

      // 2b
      if (isEmpty(filterParam.merchants)) {
        filterParam.merchants = extraRewardsIds;
        return filterParam;
      }

      // 2c
      const filteredMerchantsWithExtraRewards = filterParam.merchants.filter((id) => extraRewardsIds.includes(id));
      if (filteredMerchantsWithExtraRewards.length) {
        filterParam.merchants = filteredMerchantsWithExtraRewards;
        return filterParam;
      }

      // 2d
      return NO_EXTRA_REWARDS_MERCHANTS_MATCHES;
    }

    function updateFilterParam(filterParam = {}) {
      // If there is a top-level category_id in URL and no sub-categories are selected in filters
      // we should add this top-level category to each API request
      if (category_id && isEmpty(filterParam.categories)) {
        filterParam.categories = [category_id];
      }

      // Filter out facets with empty values from filter param
      // eslint-disable-next-line no-param-reassign
      filterParam = Object.fromEntries(Object.entries(filterParam).filter(([, value]) => !isEmpty(value)));

      return filterParam;
    }

    function stringifyFilterParamForApiCall(filterParam = {}) {
      // We do extra rewards filtering completely on FE so should not pass it as part of API filter param
      delete filterParam[EXTRA_REWARDS_FILTER];

      if (isEmpty(filterParam)) {
        return null;
      }

      // Stringify f param properly if has data in it
      return Object.entries(filterParam)
        .filter(([, value]) => !isEmpty(value))
        .map(([key, value]) => {
          // We receive 'marchants' as facet.name, but f parameter
          // for filtering on API side needs it to be 'merchant_id'
          const facetName = key === 'merchants' ? 'merchant_id' : key;
          return `${facetName}:${value.join(',')}`;
        })
        .join(';');
    }
  };

  // Fetch initial product search results with metadata on page load
  useEffect(() => {
    if (!isLoaded && !isLoading) {
      handleFetchingData(searchParamsRef.current, loadOptions);
    }
  }, []);

  // Fetch data on filter change
  useEffect(() => {
    const params = {
      ...searchParamsRef.current,
      limit: defaultLimit,
      offset: defaultOffset,
      f: filters,
    };

    handleFetchingData(params, filterOptions);
  }, [filters]);

  // Fetch more products on button click
  const fetchMoreProducts = () => {
    const params = {
      ...searchParamsRef.current,
      offset: searchParamsRef.current.offset + searchParamsRef.current.limit,
      limit: defaultLimit,
    };

    handleFetchingData(params, updateOptions);
  };

  // Fetch products on sort change
  const fetchSortedProducts = (sortType) => {
    const params = {
      ...searchParamsRef.current,
      limit: defaultLimit,
      offset: defaultOffset,
      sort_by: sortType,
    };

    handleFetchingData(params, filterOptions);
  };

  return {
    searchParams: searchParamsRef.current,
    fetchMoreProducts,
    fetchSortedProducts,
  };
};
